import React, { useState, useEffect } from "react";
import { Box, Button, Checkbox, FormControlLabel, MenuItem, Typography } from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Field as FormField,
  FormActions,
  FormCard,
  FormGrid,
  SelectInput,
  TextInput,
} from "../ui/form";
import { tokens } from "../../theme";
import supabase from "../../supabase";
import { ErrorModal } from "../ModalAlert";
import { getClientDataByCuit } from "../../apiRequests/post";
import { LoaderSpinner } from "../spinner/loader";
import clientSchema, { getClientSchema } from "./schema";
import CuitLookup from "./cuitLookup";
import { colors } from "../../constants/colors";
import provinces from "../../constants/provinces";
import { useSelector } from "react-redux";

const errorStyles = {
  margin: 0,
  fontSize: "12px",
  color: colors.red,
  paddingLeft: "5px",
};

// Campos de facturacion que casi siempre repiten el dato principal.
const COPIA_DESDE = {
  emailBilling: { origen: "email", texto: "Copiar del Email principal" },
  phoneBilling: { origen: "phone", texto: "Copiar del Teléfono principal" },
};

const initialValues = {
  name: "",
  lastName: "",
  company: "",
  address: "",
  province: "",
  ivaCondition: "",
  cuit: "",
  email: "",
  emailBilling: "",
  secondaryEmailBilling: "",
  phone: "",
  phoneBilling: "",
  balance: 0,
  test: false,
};

const EditClient = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const auth = useSelector((state) => state.auth);
  
  const isAdmin = auth?.isAdmin || sessionStorage.getItem("isAdmin") === "true";
  
  const data = location.state;
  
  const getInitialValues = () => {
    if (!data?.client) {
      return {
        ...initialValues,
        balance: 0
      };
    }
    
    return data.client;
  };

  const submit = async (values) => {
    setLoading(true);
    try {
      const isClientValid = await getClientDataByCuit({
        documento_tipo: "CUIT",
        documento_nro: Number(values.cuit),
      });

      if (isClientValid.error === "S") {
        ErrorModal({
          title: "Error en la validación del cliente",
          text: isClientValid?.errores[0] || "El cuit es inexistente",
        });
      } else {
        const finalBalance = !isAdmin && !data?.client ? 0 : values.balance;
        
        const finalValues = {
          ...values,
          ivaCondition:
            isClientValid.condicion_impositiva === "RESPONSABLE INSCRIPTO"
              ? "RI"
              : isClientValid.condicion_impositiva === "EXENTO"
                ? "E"
                : "M",
          phone: String(values.phone),
          phoneBilling: String(values.phoneBilling),
          cuit: String(values.cuit),
          invoicePointOfSale: process.env.REACT_APP_POINT_OF_SALE,
          balance: finalBalance,
        };

        const { data: saved, error } = data?.client
          ? await supabase
              .from("clients")
              .update(finalValues)
              .eq("clientId", data.client.clientId)
              .select()
              .single()
          : await supabase.from("clients").insert([finalValues]).select().single();

        if (error) {
          ErrorModal({ title: error.message });
        } else if (data?.returnTo) {
          // Se entró desde la orden: se vuelve con el cliente ya seleccionado.
          navigate(data.returnTo, { state: { selectedClient: saved } });
        } else {
          navigate("/clients");
        }
      }
    } catch (error) {
      ErrorModal({ title: error.message });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Un campo del formulario. Todos usan las mismas piezas compartidas, así que
   * el alto, el foco y el error se ven igual acá que en caja o en una orden.
   */
  const renderField = (name, label, type = "text", options = {}) => {
    if ((name === "balance" || name === "test") && !isAdmin) {
      return null;
    }

    if (type === "checkbox") {
      return (
        <Field name={name}>
          {({ field, form }) => (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    {...field}
                    checked={Boolean(field.value)}
                    onChange={(e) => form.setFieldValue(name, e.target.checked)}
                    sx={{ color: tokens.ink4, "&.Mui-checked": { color: tokens.brand } }}
                  />
                }
                label={label}
                sx={{
                  marginLeft: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "13.5px", fontWeight: 500 },
                }}
              />
              {options.hint && (
                <Typography sx={{ fontSize: "11.5px", color: tokens.ink3, paddingLeft: "32px" }}>
                  {options.hint}
                </Typography>
              )}
            </Box>
          )}
        </Field>
      );
    }

    if (name === "province") {
      return (
        <Field name={name}>
          {({ field, form }) => {
            const error = form.touched[name] && form.errors[name];
            return (
              <FormField label={label} error={error} required={options.required}>
                <SelectInput {...field} invalid={Boolean(error)}>
                  <option value="" disabled>
                    Seleccioná una provincia
                  </option>
                  {provinces.map((province) => (
                    <option key={province.id} value={province.id}>
                      {province.name}
                    </option>
                  ))}
                </SelectInput>
              </FormField>
            );
          }}
        </Field>
      );
    }

    const copia = COPIA_DESDE[name];

    return (
      <Field name={name}>
        {({ field, form }) => {
          const error = form.touched[name] && form.errors[name];
          const origen = copia ? form.values[copia.origen] : null;

          return (
            <FormField
              label={label}
              error={error}
              hint={options.hint}
              required={options.required}
              span={options.span}
              action={
                copia ? (
                  <Box
                    component="button"
                    type="button"
                    disabled={!origen}
                    title={origen ? `Copiar "${origen}"` : "Primero completá el campo principal"}
                    onClick={() => form.setFieldValue(name, origen)}
                    sx={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontFamily: "inherit",
                      fontSize: "11.5px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      color: origen ? tokens.brandDark : tokens.ink4,
                      cursor: origen ? "pointer" : "default",
                      "&:hover:not(:disabled)": { textDecoration: "underline" },
                    }}
                  >
                    {copia.texto}
                  </Box>
                ) : null
              }
            >
              <TextInput
                {...field}
                value={field.value ?? ""}
                type={type === "number" ? "text" : type}
                inputMode={type === "number" ? "numeric" : undefined}
                numeric={type === "number"}
                invalid={Boolean(error)}
                placeholder={options.placeholder}
              />
            </FormField>
          );
        }}
      </Field>
    );
  };

  const schema = getClientSchema(isAdmin);
  const isEdition = Boolean(data?.client);

  return (
    <Box className="edit_form_container" sx={{ width: "100%" }}>
      <Formik initialValues={getInitialValues()} validationSchema={schema} onSubmit={submit}>
        {({ handleSubmit }) => (
          <Form>
            {loading ? (
              <Box
                sx={{
                  width: "100%",
                  height: "200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoaderSpinner />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <FormCard
                  title="Identificación"
                  subtitle="Buscá por CUIT y ARCA completa razón social y domicilio."
                >
                  <CuitLookup errorStyles={errorStyles} />
                  <FormGrid columns={3}>
                    {renderField("company", "Razón social", "text", { span: 3, required: true })}
                    {renderField("name", "Nombre de contacto")}
                    {renderField("lastName", "Apellido")}
                  </FormGrid>
                </FormCard>

                <FormCard title="Domicilio">
                  <FormGrid columns={3}>
                    {renderField("address", "Dirección", "text", { span: 2 })}
                    {renderField("province", "Provincia")}
                  </FormGrid>
                </FormCard>

                <FormCard title="Contacto">
                  <FormGrid columns={2}>
                    {renderField("email", "Email", "email")}
                    {renderField("phone", "Teléfono", "number")}
                  </FormGrid>
                </FormCard>

                <FormCard
                  title="Facturación"
                  subtitle="A estas direcciones se envían las órdenes y las facturas."
                >
                  <FormGrid columns={2}>
                    {renderField("emailBilling", "Email para facturar", "email")}
                    {renderField("phoneBilling", "Teléfono para facturar", "number")}
                    {renderField("secondaryEmailBilling", "Email secundario", "email", {
                      span: 2,
                      hint: "Copia de órdenes y facturas a una segunda casilla.",
                    })}
                  </FormGrid>
                </FormCard>

                {isAdmin && (
                  <FormCard title="Administración">
                    <FormGrid columns={2}>
                      {renderField("balance", "Saldo del cliente", "number", {
                        hint: "Saldo a favor disponible para descontar de una factura.",
                      })}
                      {renderField("test", "Cliente de prueba", "checkbox", {
                        hint: "No se cuenta en los reportes.",
                      })}
                    </FormGrid>
                  </FormCard>
                )}

                <FormActions>
                  <Button variant="outlined" onClick={() => navigate(-1)} sx={{ minHeight: 44 }}>
                    Cancelar
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    onClick={handleSubmit}
                    sx={{ minHeight: 44, flexGrow: { xs: 1, md: 0 }, minWidth: 180 }}
                  >
                    {isEdition ? "Guardar cambios" : "Crear cliente"}
                  </Button>
                </FormActions>
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default EditClient;
